import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["input"]
  static values = {
    placeholders: String,
    interval: Number
  }

  connect() {
    this.currentIndex = 0
    this.isAnimating = false

    // Parse placeholders from JSON string and shuffle
    this.placeholders = []
    if (this.hasPlaceholdersValue) {
      try {
        this.placeholders = this.shuffle(JSON.parse(this.placeholdersValue))
      } catch (e) {
        console.error("Failed to parse placeholders:", e)
      }
    }

    // Default interval
    this.interval = this.hasIntervalValue ? this.intervalValue : 4000

    if (this.placeholders.length > 0 && this.hasInputTarget) {
      // Create overlay element for animated placeholder
      this.createOverlay()

      // Set initial text
      this.setText(this.placeholders[0])

      // Clear actual placeholder since we're using overlay
      this.inputTarget.placeholder = ""

      // Bind events
      this.boundHandleInput = this.handleInput.bind(this)
      this.boundHandleKeydown = this.handleKeydown.bind(this)
      this.inputTarget.addEventListener("input", this.boundHandleInput)
      this.inputTarget.addEventListener("keydown", this.boundHandleKeydown)

      // Start rotation if more than one placeholder
      if (this.placeholders.length > 1) {
        this.startRotation()
      }
    }
  }

  disconnect() {
    this.stopRotation()
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.remove()
    }
    if (this.hasInputTarget) {
      if (this.boundHandleInput) this.inputTarget.removeEventListener("input", this.boundHandleInput)
      if (this.boundHandleKeydown) this.inputTarget.removeEventListener("keydown", this.boundHandleKeydown)
    }
  }

  createOverlay() {
    // Get computed styles from textarea
    const styles = window.getComputedStyle(this.inputTarget)

    this.overlay = document.createElement("span")
    this.overlay.className = "rotating-placeholder-overlay"

    // Position overlay exactly where textarea text starts
    this.overlay.style.cssText = `
      position: absolute;
      top: ${styles.paddingTop};
      left: ${styles.paddingLeft};
      right: ${styles.paddingRight};
      pointer-events: none;
      color: var(--text-color, #555);
      opacity: 0.8;
      font-size: ${styles.fontSize};
      font-family: ${styles.fontFamily};
      line-height: ${styles.lineHeight};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
      z-index: 1;
    `

    // Make textarea wrapper relative and add overlay
    const wrapper = this.inputTarget.parentElement
    wrapper.style.position = "relative"
    wrapper.appendChild(this.overlay)
  }

  startRotation() {
    this.rotationTimer = setInterval(() => {
      this.rotateToNext()
    }, this.interval)
  }

  stopRotation() {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer)
    }
  }

  rotateToNext() {
    // Don't rotate if user is typing or animation in progress
    if (!this.hasInputTarget || this.inputTarget.value.length > 0 || this.isAnimating) return

    this.currentIndex = (this.currentIndex + 1) % this.placeholders.length
    this.flipTo(this.placeholders[this.currentIndex])
  }

  flipTo(newText) {
    if (!this.overlay) return
    this.isAnimating = true

    // Flip out
    this.overlay.style.transition = "transform 0.2s ease-in, opacity 0.2s ease-in"
    this.overlay.style.transform = "translateY(-8px)"
    this.overlay.style.opacity = "0"

    setTimeout(() => {
      // Change text while hidden
      this.overlay.textContent = newText

      // Reset position for flip in
      this.overlay.style.transition = "none"
      this.overlay.style.transform = "translateY(8px)"

      // Force reflow
      this.overlay.offsetHeight

      // Flip in
      this.overlay.style.transition = "transform 0.2s ease-out, opacity 0.2s ease-out"
      this.overlay.style.transform = "translateY(0)"
      this.overlay.style.opacity = "0.8"

      setTimeout(() => {
        this.isAnimating = false
      }, 200)
    }, 200)
  }

  setText(text) {
    if (this.overlay) {
      this.overlay.textContent = text
    }
  }

  handleKeydown(event) {
    if (event.key !== "Tab") return
    if (!this.overlay || this.inputTarget.value.length > 0) return

    const placeholderText = this.overlay.textContent
    if (!placeholderText) return

    event.preventDefault()
    this.inputTarget.value = placeholderText
    this.overlay.style.display = "none"
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("keyup", { bubbles: true }))
  }

  handleInput() {
    if (!this.overlay) return
    if (this.inputTarget.value.length > 0) {
      this.overlay.style.display = "none"
    } else {
      this.overlay.style.display = "block"
    }
  }

  shuffle(array) {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }
}
